import { fromSpecific, lazy, scope, TypedNode } from '@rehaje/core'
import { reactive } from '@rehaje/reactive'

import { MustacheNode } from '../Mustache'

import type { EventNode, EventSpecific } from '../Event/TypedNode'
import type { JavaScriptNode, JavaScriptSpecific } from '../JavaScript'
import type { AnyExpressionSpecific } from '../Mustache'
import type { QueryPlugin } from './plugin'
import type { QueryModel } from './ViewModel'
import type {
  AnyTypedNode,
  TypedSpecific,
  TypedSpecificLike
} from '@rehaje/core'
import type { RecursionType, TRecord } from '@rehaje/shared'

export type AnyQueryNode = AnyTypedNode & { type: `Query.${string}` }

export type AnyQuerySpecific = TypedSpecific & { type: `Query.${string}` }

export interface QuerySpecific extends QuerySpecificLike {
  type: `Query`
}

export interface QuerySpecificLike extends TypedSpecificLike {
  /**
   * 自动刷新，包含以下能力
   * 1. 页面首次加载自动请求
   * 2. 依赖参数变化时自动请求
   * 3. 其他自动请求配置能力/SWR能力等
   */
  autoRefresh?: AnyExpressionSpecific | boolean
  /**
   * 设置缓存数据回收时间。默认缓存数据 5 分钟后回收
   * 如果设置为 -1, 则表示缓存数据永不过期
   */
  cacheTime?: AnyExpressionSpecific | number

  /**
   * 请求防抖时间间隔
   */
  debounceTime?: AnyExpressionSpecific | number

  /**
   * 错误最大重试次数
   */
  errorRetryCount?: AnyExpressionSpecific | number

  /**
   * 错误重试间隔时间
   */
  errorRetryIntervalTime?: AnyExpressionSpecific | number

  /**
   * 请求轮询次数
   */
  intervalCount?: AnyExpressionSpecific | number
  /**
   * 请求轮询时间间隔
   */
  intervalTime?: AnyExpressionSpecific | number
  /**
   * loading 的防抖时间间隔
   */
  loadingDebounceTime?: AnyExpressionSpecific | number
  /**
   * 失败事件
   */
  onError?: (() => void) | EventSpecific
  /**
   * 成功事件
   */
  onSuccess?: (() => void) | EventSpecific
  /**
   * 请求参数
   */
  params?: TRecord
  /**
   * 扩展配置
   */
  props?: RecursionType<TypedSpecific>
  /**
   * 网页隐藏时自动刷新
   */
  refreshOnHidden?: AnyExpressionSpecific | boolean
  /**
   * 网络离线时自动刷新
   */
  refreshOnOffline?: AnyExpressionSpecific | boolean
  /**
   * 网络重连时自动刷新
   */
  refreshOnReconnect?: AnyExpressionSpecific | boolean
  /**
   * window 聚焦时自动刷新
   */
  refreshOnWindowFocus?: AnyExpressionSpecific | boolean
  /**
   * 请求响应器
   */
  resolver?: ((variables: TRecord) => any) | AnyExpressionSpecific
  /**
   * 错误时是否支持重试
   */
  shouldRetryOnError?: AnyExpressionSpecific | boolean
  /**
   * 缓存数据新鲜时间
   */
  staleTime?: AnyExpressionSpecific | number
  /**
   * 请求节流时间间隔
   */
  throttleTime?: AnyExpressionSpecific | number
  /**
   * 超时
   */
  timeout?: AnyExpressionSpecific | number
  /**
   * 数据转换器
   */
  transformer?: JavaScriptSpecific
  type: 'Query' | `Query.${string}`
}

@scope({
  variables() {
    return [
      {
        get() {
          return this.getExposedViewModel()
        },
        group: this.type,
        name() {
          return this.id
        },
        type: 'Block'
      },
      {
        get() {
          return this.getParams()
        },
        group: this.type,
        name: 'params',
        type: 'Local'
      }
    ]
  }
})
export class QueryNode extends TypedNode {
  /**
   * 自动刷新，包含以下能力
   * 1. 页面首次加载自动请求
   * 2. 依赖参数变化时自动请求
   * 3. 其他自动请求配置能力/SWR能力等
   */
  @fromSpecific()
  @reactive()
  autoRefresh = false

  /**
   * 设置缓存数据回收时间。默认缓存数据 5 分钟后回收
   * 如果设置为 -1, 则表示缓存数据永不过期
   */
  @fromSpecific()
  @reactive()
  cacheTime: MustacheNode | number = 300_000

  /**
   * 请求防抖时间间隔
   */
  @fromSpecific()
  @reactive()
  debounceTime: MustacheNode | number = 0

  /**
   * 错误最大重试次数
   */
  @fromSpecific()
  @reactive()
  errorRetryCount?: boolean | MustacheNode

  /**
   * 错误重试间隔时间
   */
  @fromSpecific()
  @reactive()
  errorRetryIntervalTime?: MustacheNode | number

  /**
   * 请求轮询次数
   */
  @fromSpecific()
  @reactive()
  intervalCount: MustacheNode | number = 0

  /**
   * 请求轮询时间间隔
   */
  @fromSpecific()
  @reactive()
  intervalTime: MustacheNode | number = 0

  /**
   * loading 的防抖时间间隔
   */
  @fromSpecific()
  @reactive()
  loadingDebounceTime: MustacheNode | number = 360

  /**
   * 失败事件
   */
  @fromSpecific()
  @reactive()
  onError?: EventNode

  /**
   * 成功事件
   */
  @fromSpecific()
  @reactive()
  onSuccess?: EventNode

  /**
   * 请求参数
   */
  @fromSpecific()
  @reactive()
  params: TRecord = {}

  /**
   * 自定义 Query 请求参数配置
   */
  @fromSpecific()
  @lazy()
  @reactive('deep')
  props: RecursionType<AnyTypedNode> = {}

  /**
   * 网页隐藏时自动刷新，默认值等于 autoRefresh
   */
  @fromSpecific()
  @reactive()
  refreshOnHidden?: boolean | MustacheNode

  /**
   * 网络离线时自动刷新
   */
  @fromSpecific()
  @reactive()
  refreshOnOffline?: boolean | MustacheNode

  /**
   * 网络重连时自动刷新
   */
  @fromSpecific()
  @reactive()
  refreshOnReconnect?: boolean | MustacheNode

  /**
   * window 聚焦时自动刷新
   */
  @fromSpecific()
  @reactive()
  refreshOnWindowFocus?: boolean | MustacheNode

  /**
   * 请求响应器
   */
  @fromSpecific()
  @reactive()
  resolver?:
    | ((variables: TRecord, controller: AbortController) => any)
    | MustacheNode

  /**
   * 错误时是否支持重试
   */
  @fromSpecific()
  @reactive()
  shouldRetryOnError?: boolean | MustacheNode

  /**
   * 缓存数据保持新鲜时间。在该时间间隔内，认为数据是新鲜的，不会重新发请求
   * 如果设置为 -1，则表示数据永远新鲜
   */
  @fromSpecific()
  @reactive()
  staleTime: MustacheNode | number = 0

  /**
   * 请求节流时间间隔
   */
  @fromSpecific()
  @reactive()
  throttleTime: MustacheNode | number = 0

  /**
   * 超时
   */
  @fromSpecific()
  @reactive()
  timeout: MustacheNode | number = 10_000

  /**
   * 数据转换器
   */
  @fromSpecific()
  @reactive()
  transformer?: JavaScriptNode

  /**
   * 所有 mustache 子节点
   */
  get mustaches() {
    const mustaches: MustacheNode[] = []
    this.getChildren().forEach((child) => {
      if (child instanceof MustacheNode && child.getBelongs()[0] === 'props') {
        mustaches.push(child)
      }
    })
    return mustaches
  }

  override fromSnapshot(root: TypedNode): void {
    if (root instanceof TypedNode && root.type === 'Page') {
      root.append(this, ['queries'])
    }
  }

  override getIncreaseKey() {
    return this.getPlugin<QueryPlugin>().props.defaultIncreaseKey ?? this.type
  }

  getParams() {
    const model = this.getSelfViewModel<QueryModel>()
    const scope = this.getSelfViewScope()
    const localParams = scope?.getLocalVariable('params') ?? {}
    const modelParams = model?.params ?? {}
    return {
      ...modelParams,
      ...localParams
    }
  }
}

declare global {
  export interface RehajeExtendsSpecifics {
    ExtendsQuery: QuerySpecific
  }
}
